/**
 * BSC (FOR-011): cómo se leen en pantalla los números y las fechas del
 * módulo (contrato de tipos, docs/BSC.md §1.9).
 *
 * La API manda fracciones 0..1 con 6 decimales; el porcentaje con 1 decimal
 * es solo de la pantalla ("49.6%", "50%"). Las fechas llegan como texto
 * `yyyy-MM-dd` (DATE) o `yyyy-MM-dd HH:mm:ss` (DATETIME): se parten a mano en
 * hora local, porque `new Date("2026-07-08 10:00:00")` no lo entiende
 * cualquier navegador y `new Date("2026-07-15")` lo toma en UTC (un día antes
 * en México).
 *
 * Funciones puras, sin Angular: las comparten A1 y sus gráficas, y las
 * pantallas que lleguen después (A2, A3).
 */

const NUMERO = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 });
const FECHA = new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "short", year: "numeric" });
const DIA_MES = new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "short" });
const FECHA_TEXTO = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/** Lo que se pinta cuando no hay dato. */
export const SIN_DATO = "—";

/** Un número finito o nulo (lo que no se puede leer cuenta como sin dato). */
export function numero(valor: number | null | undefined): number | null {
  if (valor == null) {
    return null;
  }
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

/** A un decimal, sin "-0". */
function unDecimal(valor: number): number {
  const r = Math.round(valor * 10) / 10;
  return r === 0 ? 0 : r;
}

/** Fracción 0..1 a porcentaje con hasta 1 decimal: 0.496429 → "49.6%". */
export function pct(fraccion: number | null | undefined, vacio = SIN_DATO): string {
  const n = numero(fraccion);
  return n == null ? vacio : `${NUMERO.format(unDecimal(n * 100))}%`;
}

/** Diferencia de fracciones en puntos porcentuales con signo: -0.04 → "-4 pts", 0.015 → "+1.5 pts". */
export function pts(fraccion: number | null | undefined, vacio = SIN_DATO): string {
  const n = numero(fraccion);
  if (n == null) {
    return vacio;
  }
  const v = unDecimal(n * 100);
  return `${v > 0 ? "+" : ""}${NUMERO.format(v)} pts`;
}

/** `yyyy-MM-dd[ HH:mm:ss]` a fecha local; nula si el texto no es una fecha. */
export function aFecha(texto: string | null | undefined): Date | null {
  const m = FECHA_TEXTO.exec(texto ?? "");
  if (!m) {
    return null;
  }
  const fecha = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(fecha.getTime()) ? null : fecha;
}

/** "2026-07-15" o "2026-07-08 10:00:00" → "15 jul 2026"; `vacio` si no hay fecha. */
export function fechaCorta(texto: string | null | undefined, vacio = SIN_DATO): string {
  const fecha = aFecha(texto);
  return fecha ? FECHA.format(fecha) : vacio;
}

/**
 * Para fechas dentro del año que se está viendo (F6.4, Mi BSC): "2026-10-15"
 * → "15 oct". Si la fecha es de otro año que `anio` (la captura de diciembre
 * vence en enero), lleva el año: "5 ene 2027". `vacio` si no hay fecha.
 */
export function diaMes(texto: string | null | undefined, anio: number | null = null, vacio = SIN_DATO): string {
  const fecha = aFecha(texto);
  if (!fecha) {
    return vacio;
  }
  return anio != null && fecha.getFullYear() !== anio ? FECHA.format(fecha) : DIA_MES.format(fecha);
}

/**
 * Para bitácoras (F4.3): "2026-07-08 10:05:00" → "8 jul 2026, 10:05". Sin
 * hora, o con 00:00 (renglones sembrados desde una columna DATE, como
 * FechaBitacoraPipe), solo el día.
 */
export function fechaHora(texto: string | null | undefined, vacio = SIN_DATO): string {
  const m = FECHA_TEXTO.exec(texto ?? "");
  const dia = fechaCorta(texto, "");
  if (!m || !dia) {
    return vacio;
  }
  return m[4] != null && `${m[4]}:${m[5]}` !== "00:00" ? `${dia}, ${m[4]}:${m[5]}` : dia;
}

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** Mes calendario 1..12 → "Abril" (con mayúscula, para el título de una tarjeta). */
export function nombreMes(mes: number | null | undefined): string {
  const nombre = mes != null ? MESES[mes - 1] : undefined;
  return nombre ? nombre.charAt(0).toUpperCase() + nombre.slice(1) : SIN_DATO;
}

/** Mes calendario 1..12 → "septiembre" (en minúscula, para una frase: "capturar septiembre"). */
export function mesEnTexto(mes: number | null | undefined): string {
  return (mes != null ? MESES[mes - 1] : undefined) ?? SIN_DATO;
}

/** Trimestre 1..4 de un mes calendario 1..12 (BscCalculadora.mesATrimestre). */
export function trimestreDeMes(mes: number): number {
  return Math.floor((mes - 1) / 3) + 1;
}

/** Los 3 meses calendario de un trimestre 1..4 (BscCalculadora.mesDe). */
export function mesesDeTrimestre(q: number): number[] {
  return [0, 1, 2].map((o) => (q - 1) * 3 + o + 1);
}

/** Trimestre 1..4 → "abril a junio". */
export function mesesDelTrimestre(q: number): string {
  return `${MESES[(q - 1) * 3]} a ${MESES[(q - 1) * 3 + 2]}`;
}

/** La unidad como se lee: "%", "No" o "días" (BscUnidad guarda `dias`). */
export function unidad(valor: string | null | undefined): string {
  if (!valor) {
    return "%";
  }
  return valor === "dias" ? "días" : valor;
}

/**
 * La meta anual de un KPI como se lee (F4.3, A2 y A3): en % la fracción
 * ("95%"); en otra unidad, el texto con que se escribió si lo hay ("6
 * productos") o el número con su unidad ("12 días"); sin valor, el texto o
 * `vacio`.
 */
export function meta(
  valor: number | null | undefined,
  texto: string | null | undefined,
  unidadMeta: string | null | undefined,
  vacio = SIN_DATO,
): string {
  const n = numero(valor);
  const escrito = (texto ?? "").trim();
  if (n == null) {
    return escrito || vacio;
  }
  if (!unidadMeta || unidadMeta === "%") {
    return pct(n);
  }
  return escrito || `${NUMERO.format(n)} ${unidad(unidadMeta)}`;
}

/** Una lista de nombres para una frase: "A", "A y B", "A, B y 3 más". */
export function nombres(lista: string[], tope = 2): string {
  if (lista.length <= tope) {
    return lista.length === 2 ? `${lista[0]} y ${lista[1]}` : lista.join("");
  }
  return `${lista.slice(0, tope).join(", ")} y ${lista.length - tope} más`;
}

/* ---------- Campos numéricos de la captura (F6.4 el cuadro de captura, F6.5 C3) ----------
 * El real y el programado se escriben en % y viajan como fracción; las
 * unidades, tal cual. Mismas reglas en el cuadro y en la tabla de C3. */

/**
 * Tope del avance real (I) de un KPI en %, como se escribe: 1000 %. Es el
 * TOPE_REAL_EN_FRACCION de BscCalculadora (10) × 100. Arriba de 100 % es
 * sobrecumplimiento y se guarda con el aviso SUPERA_PROGRAMADO; arriba del
 * tope, real.do lo rechaza (REAL_FUERA_DE_RANGO).
 */
export const TOPE_REAL_EN_PORCENTAJE = 1000;

/** Lo que se leyó de un campo numérico: el número, vacío (nulo, sin error) o lo que no se entiende (error). */
export interface LecturaNumero {
  valor: number | null;
  error: string;
}

/**
 * "90", "90%", "90.5", "90,5" → número; vacío → nulo; lo demás, error. Sin
 * separador de miles: "1,000" sería mil o uno.
 */
export function leeNumero(texto: string): LecturaNumero {
  const t = (texto ?? "").replace(/[\s  ]/g, "");
  if (!t) {
    return { valor: null, error: "" };
  }
  if (!/^-?\d+(?:[.,]\d+)?$/.test(t)) {
    return { valor: null, error: "escribe solo el número (p. ej. 90 o 87.5)." };
  }
  return { valor: Number(t.replace(",", ".")), error: "" };
}

/** Como leeNumero, admitiendo el % al final: "90 %" → 90. */
export function leePorcentaje(texto: string): LecturaNumero {
  return leeNumero((texto ?? "").trim().replace(/%$/, ""));
}

/** Fracción → texto en % para el campo (0.875 → "87.5"); vacío sin valor. */
export function textoPorcentaje(fraccion: number | null | undefined): string {
  const n = numero(fraccion);
  return n == null ? "" : formatoNumero(n * 100);
}

/** Un número para el campo (las unidades); vacío sin valor. */
export function textoNumero(valor: number | null | undefined): string {
  const n = numero(valor);
  return n == null ? "" : formatoNumero(n);
}

/** Sin ruido del punto flotante ni separador de miles: 33.33333 → "33.3333". */
export function formatoNumero(n: number): string {
  return `${Number(n.toFixed(4))}`;
}
